import { LegacyCardContent } from "../../card/LegacyCard";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import React, { ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import PaymentStatusIndicator from "../../../pages/order/paymentStatusIndicator/PaymentStatusIndicator";
import { Alert } from "../../alert/Alert";
import { DropdownLegacy } from "../../dropdown/DropdownLegacy";
import { Card, CardHeader, H5, Icon, SmallButton } from "@sellar-io/components";
import { PaymentTermsIndicator } from "../../paymentTypeIndicator/PaymentTermsIndicator";
import { ProfilePicture } from "../../profilePicture/ProfilePicture";
import { OrderStage } from "../helpers";
import { OrderActionPanel } from "../OrderActionPanel";
import { OrderStatusBar } from "../OrderStatusBar";
import { OrderStatusHeader } from "../OrderStatusHeader";
import { OrderStatusText } from "../OrderStatusText";
import { OrderSummaryHeaderActionButtons } from "../OrderSummaryHeaderActionButtons";

export type OrderSummaryStatusContainerProps = {
  cancellationDate: string | null;
  deliveryDate: string;
  logoUrl: string | null;
  paymentStatus: OrderGetResponseType["paymentStatus"] | null;
  fulfillmentType?: OrderGetResponseType["fulfillmentType"];
  orderId: string;
  orderTotal: string;
  stage: OrderStage;
  requestedDaysAgo: string;
  requestDate: string;
  buyerName: string;
  buyerEmails: string[];
  supplierName: string;
  cancelledBy: OrderGetResponseType["cancelledBy"];
  cancelledAt: Date | null;
  createdAt: Date;
  onBusinessNameClicked?: () => void;
  onVatInvoiceClick?: () => void;
  dropDownChildren?: ReactNode;
  confirmedAt: Date | null;
  dispatchedAt: Date | null;
  deliveredAt: Date | null;
  paymentDue: Date | null;
  paidAt: Date | null;
  retailerPaidAt: Date | null;
  voidedAt: Date | null;
  invoiceFinalizedAt: Date | null;
  acceptedAt: Date | null;
  invoiceIssuedAt: Date | null;
  dispatchPlannedAt: Date | null;
  deliveryPlannedAt: Date | null;
  paymentType: OrderGetResponseType["paymentType"];
  stripeInvoiceId?: string;
  stripeAccountId?: string | null;
  cancellationReason: string | null;
  retailerPhoneNumber?: string | null;
  buyerPostcode: string;
  supplierId: number;
  retailerId: number;
  invoiceCreateStage: OrderGetResponseType["Supplier"]["invoiceCreationStage"];
  connection?: {
    id: number;
    paymentTerms: "credit" | "paymentOnDelivery" | "proforma" | null;
    paymentDueCalculateFrom:
      | "invoiceIssueDate"
      | "endOfMonth"
      | "endOfFollowingMonth"
      | null;
    paymentDue: number | null;
  };
  orderItems: { ProductId: number; quantity: number }[];
  invoicesEnabled: boolean;
  customInvoiceNumber: string | null;
  price: number;
  paidAmount: number;
  hasNoDraughtDutyRelief: boolean;
  supplierUrlId: string;
  deferredPaymentId: string | null;
  initiator: "supplier" | "retailer" | null;
  twoOrder: OrderGetResponseType["TwoOrder"];
  SupplierPalletShipment: OrderGetResponseType["SupplierPalletShipment"];
  dueDays: number | null;
};

export const OrderSummaryStatusContainerLegacy = (
  props: OrderSummaryStatusContainerProps,
): React.ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const [messageType, setMessageType] = useState<string>();

  const { message, title } = (() => {
    switch (messageType) {
      case "invoiceSent":
        return {
          title: "Invoice sent successfully",
          message: `Delivered to: ${props.buyerEmails[0]}. ${props.buyerName} have been notified and can also access their invoice from within the Sellar app.`,
        };
      case "reminderSent":
        return {
          title: "Reminder sent successfully",
          message: `Delivered to: ${props.buyerEmails[0]}`,
        };
      default:
        return { title: undefined, message: undefined };
    }
  })();

  return (
    <>
      {message && (
        <Alert
          className={"w-full"}
          type={"success"}
          title={title}
          message={message}
          onDismiss={() => {
            setMessageType(undefined);
          }}
        />
      )}
      <Card className="bg-white">
        <CardHeader className={twMerge("bg-white", "p-4")}>
          <div className="flex items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <ProfilePicture
                logoUrl={props.logoUrl}
                name={business?.Supplier ? props.buyerName : props.supplierName}
                size={"small"}
              />

              <div className="flex flex-col">
                <H5>
                  <span
                    className={twMerge(
                      props.onBusinessNameClicked && "cursor-pointer",
                    )}
                    onClick={props.onBusinessNameClicked}
                  >
                    {business?.Retailer ? props.supplierName : props.buyerName}
                  </span>{" "}
                  - {props.orderTotal}
                </H5>
                <div className="text-sm text-gray-500">
                  Order #{props.orderId}
                  {props.customInvoiceNumber && (
                    <strong> - Invoice ID {props.customInvoiceNumber}</strong>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className={"hidden md:block"}>
                <OrderSummaryHeaderActionButtons
                  orderId={parseInt(props.orderId)}
                  paymentDue={props.paymentDue}
                  cancelledBy={props.cancelledBy}
                  cancelledAt={props.cancelledAt}
                  voidedAt={props.voidedAt}
                  confirmedAt={props.confirmedAt}
                  dispatchedAt={props.dispatchedAt}
                  deliveredAt={props.deliveredAt}
                  invoiceFinalizedAt={props.invoiceFinalizedAt}
                  paymentType={props.paymentType}
                  deliveryPlannedAt={props.deliveryPlannedAt ?? null}
                  paymentStatus={props.paymentStatus}
                  stripeInvoiceId={props.stripeInvoiceId}
                  fulfillmentType={props.fulfillmentType}
                  retailerId={props.retailerId}
                  orderItems={props.orderItems}
                  connectionId={props.connection?.id}
                  invoiceCreationStage={props.invoiceCreateStage}
                  invoiceIssuedAt={props.invoiceIssuedAt}
                />
              </div>
              {props.dropDownChildren && (
                <DropdownLegacy className={"print:hidden"}>
                  {props.dropDownChildren}
                </DropdownLegacy>
              )}
            </div>
          </div>
          <div className={"md:hidden lg:hidden xl:hidden"}>
            <OrderSummaryHeaderActionButtons
              orderId={parseInt(props.orderId)}
              paymentDue={props.paymentDue}
              cancelledBy={props.cancelledBy}
              cancelledAt={props.cancelledAt}
              voidedAt={props.voidedAt}
              confirmedAt={props.confirmedAt}
              dispatchedAt={props.dispatchedAt}
              deliveredAt={props.deliveredAt}
              invoiceFinalizedAt={props.invoiceFinalizedAt}
              paymentType={props.paymentType}
              deliveryPlannedAt={props.deliveryPlannedAt ?? null}
              paymentStatus={props.paymentStatus}
              stripeInvoiceId={props.stripeInvoiceId}
              fulfillmentType={props.fulfillmentType}
              retailerId={props.retailerId}
              orderItems={props.orderItems}
              connectionId={props.connection?.id}
              invoiceCreationStage={props.invoiceCreateStage}
              invoiceIssuedAt={props.invoiceIssuedAt}
            />
          </div>
        </CardHeader>
        <LegacyCardContent>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              <PaymentTermsIndicator
                connection={props.connection}
                invoiceCreationStage={props.invoiceCreateStage}
              />
              <PaymentStatusIndicator
                paymentStatus={props.paymentStatus}
                voidedAt={props.voidedAt}
                paidAt={props.paidAt}
                retailerPaidAt={props.retailerPaidAt}
                paymentType={props.paymentType}
                paymentDue={props.paymentDue}
                cancelledAt={props.cancelledAt}
                invoiceIssuedAt={props.invoiceIssuedAt}
                invoicesEnabled={props.invoicesEnabled}
                showOverdueStatus={true}
              />
            </div>
            <OrderStatusHeader
              stage={props.stage}
              fulfillmentType={props.fulfillmentType}
            />
            <OrderStatusBar stage={props.stage} />
            <OrderStatusText
              cancelledAt={props.cancelledAt}
              voidedAt={props.voidedAt}
              dispatchPlannedAt={props.dispatchPlannedAt}
              deliveryPlannedAt={props.deliveryPlannedAt}
              deliveredAt={props.deliveredAt}
              createdAt={props.createdAt}
              stage={props.stage}
              supplierName={props.supplierName}
              buyerName={props.buyerName}
              cancelledBy={props.cancelledBy}
              cancellationReason={props.cancellationReason}
              acceptedAt={props.acceptedAt}
            />
            {props.onVatInvoiceClick && (
              <div>
                <SmallButton>
                  <Icon icon={"OPEN_IN_NEW"} />
                  VAT INVOICE
                </SmallButton>
              </div>
            )}
            {!props.cancellationDate && !props.voidedAt && (
              <OrderActionPanel
                key={props.orderId}
                buyerName={props.buyerName}
                buyerPostcode={props.buyerPostcode}
                invoiceFinalizedAt={props.invoiceFinalizedAt}
                orderId={parseInt(props.orderId)}
                confirmedAt={props.confirmedAt}
                paymentStatus={props.paymentStatus}
                paymentDue={props.paymentDue}
                paymentType={props.paymentType}
                paymentTerms={props.connection?.paymentTerms}
                supplierName={props.supplierName}
                stripeInvoiceId={props.stripeInvoiceId}
                stripeAccountId={props.stripeAccountId}
                setMessageType={setMessageType}
                fulfillmentType={props.fulfillmentType}
                deliveredAt={props.deliveredAt}
                dispatchedAt={props.dispatchedAt}
                voidedAt={props.voidedAt}
                invoiceCreationStage={props.invoiceCreateStage}
                invoiceIssuedAt={props.invoiceIssuedAt}
                paidAt={props.paidAt}
                price={props.price}
                paidAmount={props.paidAmount}
                hasNoDraughtDutyRelief={props.hasNoDraughtDutyRelief}
                acceptedAt={props.acceptedAt}
                deferredPaymentId={props.deferredPaymentId}
                initiator={props.initiator}
                dispatchPlannedAt={null}
                twoOrder={props.twoOrder}
                SupplierPalletShipment={props.SupplierPalletShipment}
                supplierId={props.supplierId}
                dueDays={props.dueDays}
                hasBankDetails={false}
                invoicesEnabled={props.invoicesEnabled}
                hasPaymentLink={false}
              />
            )}
          </div>
        </LegacyCardContent>
      </Card>
    </>
  );
};
